'use client';

import { BookOpen, Calculator, ExternalLink, Lightbulb } from 'lucide-react';

interface RelatedResourcesProps {
  vectorString?: string;
  severity?: string;
}

interface ResourceLink {
  href: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  isPrimary?: boolean;
}

export default function RelatedResources({ vectorString, severity }: RelatedResourcesProps) {
  // Build CVSS Calculator URL with vector string if available
  const cvssCalculatorUrl = vectorString
    ? `/tools/security/cvss-calculator?vector=${encodeURIComponent(vectorString)}`
    : '/tools/security/cvss-calculator';

  // Core resources always shown
  const resources: ResourceLink[] = [
    {
      href: cvssCalculatorUrl,
      title: 'CVSS Calculator',
      description: vectorString
        ? 'View this score breakdown or calculate a custom score'
        : 'Calculate and understand CVSS scores',
      icon: <Calculator className="w-5 h-5" />,
      isPrimary: true,
    },
    {
      href: '/blog/cvss-scoring-system-explained',
      title: 'Understanding CVSS Scoring',
      description: 'Learn how severity scores are calculated and what they mean',
      icon: <BookOpen className="w-5 h-5" />,
    },
    {
      href: '/blog/how-do-i-prioritize-which-cves-to-patch-first',
      title: 'CVE Prioritization Guide',
      description: 'Best practices for deciding which vulnerabilities to address first',
      icon: <Lightbulb className="w-5 h-5" />,
    },
    {
      href: '/blog/what-is-a-cve-and-why-are-they-important',
      title: 'What is a CVE?',
      description: 'Essential guide to Common Vulnerabilities and Exposures',
      icon: <BookOpen className="w-5 h-5" />,
    },
  ];

  // Add severity-specific resources
  if (severity?.toUpperCase() === 'CRITICAL' || severity?.toUpperCase() === 'HIGH') {
    resources.push({
      href: '/blog/what-is-the-difference-between-cve-and-cwe',
      title: 'CVE vs CWE Explained',
      description: 'Understand how CVEs relate to underlying weakness types',
      icon: <BookOpen className="w-5 h-5" />,
    });
  }

  return (
    <section className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
        <Lightbulb className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        Learn More
      </h3>

      <div className="grid sm:grid-cols-2 gap-3">
        {resources.map((resource) => (
          <a
            key={resource.href}
            href={resource.href}
            className={`group flex items-start gap-3 p-3 rounded-lg transition-colors ${
              resource.isPrimary
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-white dark:bg-gray-800 hover:bg-blue-100 dark:hover:bg-blue-900/40 border border-blue-200 dark:border-blue-700'
            }`}
          >
            <div className={`flex-shrink-0 mt-0.5 ${
              resource.isPrimary
                ? 'text-white'
                : 'text-blue-600 dark:text-blue-400'
            }`}>
              {resource.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <span className={`font-medium text-sm ${
                  resource.isPrimary
                    ? 'text-white'
                    : 'text-gray-900 dark:text-gray-100'
                }`}>
                  {resource.title}
                </span>
                <ExternalLink className={`w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity ${
                  resource.isPrimary
                    ? 'text-white'
                    : 'text-gray-400'
                }`} />
              </div>
              <p className={`text-xs mt-0.5 ${
                resource.isPrimary
                  ? 'text-white'
                  : 'text-gray-600 dark:text-gray-400'
              }`}>
                {resource.description}
              </p>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
